import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import {
  CLUSTER_COLUMNS,
  clusterGroup,
} from '../../../packages/domain/src/56-score-board-games.mjs';

/** Guard native Bubble Grove button clicks without claiming browser pan/zoom.
 * Pointer samples authorize one unchanged, short tap; native click alone moves.
 * Keyboard/assistive clicks remain available independently of pointer history.
 * Cancellation/disposal never applies a move or captures a touch pointer. */
export function attachClusterInput(board, readState) {
  const doc = board.ownerDocument,
    listeners = [],
    touches = new Set();
  let tap = null,
    disposed = false;
  const listen = (node, name, handler, options) => {
    if (!node?.addEventListener) return;
    node.addEventListener(name, handler, options);
    listeners.push([node, name, handler, options]);
  };
  const time = (event) => (Number.isFinite(event.timeStamp) ? event.timeStamp : performance.now());
  const cellAt = (event) => {
    const cell = event.target?.closest?.('[data-cell]');
    return cell && board.contains(cell) ? cell : null;
  };
  const cancel = () => {
    if (tap) tap.cancelled = true;
  };
  const inside = (cell, event) => {
    const rect = cell?.getBoundingClientRect?.();
    return (
      !rect ||
      (event.clientX >= rect.left &&
        event.clientX < rect.right &&
        event.clientY >= rect.top &&
        event.clientY < rect.bottom)
    );
  };
  const legal = (cell, state) => {
    if (
      !cell ||
      cell.disabled ||
      board.isConnected === false ||
      state.completed ||
      state.gameId !== 'cluster-pop'
    )
      return false;
    const index = Number(cell.dataset.cell);
    if (!Number.isInteger(index) || index < 0) return false;
    const view = gameView(state).cells[index];
    return Boolean(view?.move && !view.disabled);
  };
  // Observing the document catches a second finger outside this board. These
  // listeners never cancel native events or affect another surface's controls.
  const touchDown = (event) => {
    if (event.pointerType !== 'touch') return;
    touches.add(event.pointerId);
    if (touches.size > 1 || event.isPrimary === false) cancel();
  };
  const touchEnd = (event) => touches.delete(event.pointerId);
  listen(doc, 'pointerdown', touchDown, { capture: true, passive: true });
  listen(doc, 'pointerup', touchEnd, { capture: true, passive: true });
  listen(doc, 'pointercancel', touchEnd, { capture: true, passive: true });
  listen(
    board,
    'pointerdown',
    (event) => {
      // The board fallback also supports detached test/document adapters.
      touchDown(event);
      if (event.button !== 0 || event.isPrimary === false || touches.size > 1) {
        cancel();
        return;
      }
      const state = readState(),
        cell = cellAt(event);
      tap = {
        id: event.pointerId,
        cell,
        seed: state.seed,
        moves: state.moves,
        x: event.clientX,
        y: event.clientY,
        started: time(event),
        released: false,
        cancelled: !legal(cell, state) || !inside(cell, event),
      };
    },
    { passive: true },
  );
  const sample = (event) => {
    if (!tap || tap.id !== event.pointerId || tap.released) return;
    for (const point of [...(event.getCoalescedEvents?.() || []), event])
      if (Math.hypot(point.clientX - tap.x, point.clientY - tap.y) >= 8) cancel();
  };
  listen(board, 'pointermove', sample, { passive: true });
  listen(
    board,
    'pointerup',
    (event) => {
      touchEnd(event);
      if (!tap || tap.id !== event.pointerId) return;
      sample(event);
      const elapsed = time(event) - tap.started;
      if (
        event.button !== 0 ||
        event.isPrimary === false ||
        elapsed < 0 ||
        elapsed > 500 ||
        cellAt(event) !== tap.cell ||
        !inside(tap.cell, event)
      )
        cancel();
      tap.released = true;
    },
    { passive: true },
  );
  for (const name of ['pointercancel', 'lostpointercapture', 'pointerleave'])
    listen(
      board,
      name,
      (event) => {
        if (tap?.id === event.pointerId && !tap.released) cancel();
        if (name === 'pointercancel') touchEnd(event);
      },
      { passive: true },
    );
  listen(
    board,
    'click',
    (event) => {
      const state = readState(),
        cell = cellAt(event),
        current = tap;
      tap = null;
      const valid =
        !disposed &&
        legal(cell, state) &&
        (event.detail === 0 ||
          (current?.released &&
            !current.cancelled &&
            current.cell === cell &&
            current.seed === state.seed &&
            current.moves === state.moves &&
            event.button === 0 &&
            event.isPrimary !== false));
      if (!valid) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );
  for (const name of ['selectstart', 'contextmenu', 'dragstart'])
    listen(board, name, (event) => {
      if (!cellAt(event)) return;
      cancel();
      event.preventDefault();
    });
  listen(board, 'keydown', (event) => {
    const directions = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -CLUSTER_COLUMNS,
      ArrowDown: CLUSTER_COLUMNS,
    };
    if (
      !(event.key in directions) ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
    )
      return;
    const cell = cellAt(event),
      state = readState();
    if (!legal(cell, state)) return;
    const view = gameView(state),
      index = Number(cell.dataset.cell),
      group = new Set(clusterGroup(state.data.board, index)),
      step = directions[event.key],
      horizontal = Math.abs(step) === 1;
    event.preventDefault();
    cancel();
    for (let next = index + step; next >= 0 && next < view.cells.length; next += step) {
      if (horizontal && Math.floor(next / view.columns) !== Math.floor(index / view.columns)) break;
      if (group.has(next) || view.cells[next].disabled) continue;
      const target = board.querySelector?.(`[data-cell="${next}"]`);
      if (target && !target.disabled) {
        target.focus();
        break;
      }
    }
  });
  listen(
    doc,
    'scroll',
    (event) => {
      if (event.target === doc || event.target?.contains?.(board)) cancel();
    },
    { capture: true, passive: true },
  );
  const suspend = () => {
    cancel();
    touches.clear();
  };
  listen(doc, 'visibilitychange', () => {
    if (doc.hidden) suspend();
  });
  listen(doc?.defaultView, 'blur', suspend);
  const dispose = () => {
    disposed = true;
    cancel();
    touches.clear();
    for (const [node, name, handler, options] of listeners)
      node.removeEventListener(name, handler, options);
    listeners.length = 0;
  };
  dispose.cancel = cancel;
  return dispose;
}
